import { lazy, Suspense, useEffect } from 'react';
import { useAuthStore } from '@/state/authStore.js';
import { isValidTab, useUiStore } from '@/state/uiStore.js';
import { useToast } from '@/components/ui/useToast.js';
import { onUnauthorized } from '@/api/client.js';

import TitleBar from '@/components/shell/TitleBar.jsx';
import Sidebar from '@/components/shell/Sidebar.jsx';
import TabHost from '@/components/shell/TabHost.jsx';
import GlobalSearchModal from '@/components/shell/GlobalSearchModal.jsx';
import HelpMount from '@/components/help/HelpMount.jsx';
import DylanEmbed from '@/components/shell/DylanEmbed.jsx';
import { tabForHelpSection, useHelpStore } from '@/state/helpStore.js';
import { searchUsers } from '@/api/endpoints/userManagement.js';
import { useUserMgmtStore } from '@/tabs/UserManagement/state.js';
import { useWhatsNewStore } from '@/state/whatsNewStore.js';
import { useFeaturePreviewStore } from '@/state/featurePreviewStore.js';

import ToastContainer from '@/components/ui/ToastContainer.jsx';
import ConfirmModal from '@/components/ui/ConfirmModal.jsx';
import BusyOverlay from '@/components/ui/BusyOverlay.jsx';
import ErrorBoundary from '@/components/common/ErrorBoundary.jsx';
import WhatsNewModal from '@/components/whats-new/WhatsNewModal.jsx';

const AdminSettingsModal = lazy(() => import('@/admin/AdminSettingsModal.jsx'));
// Seasonal decoration, switched on per site in Admin Settings. Each theme's
// drawing is its own chunk, downloaded only while that theme is showing.
const SeasonalTheme = lazy(() => import('@/components/seasonal/SeasonalTheme.jsx'));
const RemoteToolsModal = lazy(() => import('@/tabs/UserManagement/detail/sections/RemoteToolsModal.jsx'));

/**
 * Root composition. Owns:
 *  - Auth bootstrap (calls /api/user-info on mount, populates authStore)
 *  - Persistent shell (title bar + sidebar + tab host)
 *  - Global overlays (toasts, confirm modal, global search modal)
 *
 * Render flow:
 *  - While auth is bootstrapping → fullscreen boot loader
 *  - On auth error (not 401 — that's handled by the API client redirect) →
 *    error panel with retry
 *  - Otherwise → full shell
 */
export default function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const initialised = useAuthStore((s) => s.initialised);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const user = useAuthStore((s) => s.user);
  const openGlobalSearch = useUiStore((s) => s.openGlobalSearch);
  const adminSettingsOpen = useUiStore((s) => s.adminSettingsOpen);
  const adminSettingsTab = useUiStore((s) => s.adminSettingsTab);
  const openAdminSettings = useUiStore((s) => s.openAdminSettings);
  const closeAdminSettings = useUiStore((s) => s.closeAdminSettings);
  const remoteToolsTarget = useUiStore((s) => s.remoteToolsTarget);
  const closeRemoteTools = useUiStore((s) => s.closeRemoteTools);
  const openHelp = useHelpStore((s) => s.openHelp);
  const toast = useToast();

  // Kick off the auth handshake on first mount.
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Once signed in, ask whether there's an unseen What's New feature to show —
  // checked once per app load (the store no-ops on a second call), not on
  // every render or tab change. See state/whatsNewStore.js.
  useEffect(() => {
    if (!user) return;
    useWhatsNewStore.getState().checkOnLogin();
    useFeaturePreviewStore.getState().load();
  }, [user]);

  // Hook a global handler so the API client can also surface session
  // expiry as a toast just before redirecting (the redirect itself is
  // already configured in api/client.js).
  useEffect(() => {
    const unsub = onUnauthorized(() => {
      toast.warning('Session expired', 'Redirecting to sign in…');
    });
    return unsub;
  }, [toast]);

  /**
   * Cross-app deep link: `/?remoteTools=<agentHostname>[&deviceName=<label>]`
   * opens the full Remote Tools modal on load. This is the entry point used by
   * OmniHelp (the service desk) so an operator can jump from a ticket's device
   * straight into Remote Tools here.
   *
   * `remoteTools` must be the AGENT REGISTRY hostname (Android agents key on
   * `android-<serial>`, not the Intune deviceName) — callers resolve it first
   * via GET /api/remote-tools/resolve. Gated on `user` because this component
   * early-returns the boot loader until auth resolves. The param is stripped
   * afterwards so closing the modal and refreshing doesn't reopen it.
   */
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const hostname = params.get('remoteTools');
    if (!hostname) return;
    useUiStore.getState().openRemoteTools({
      hostname,
      deviceName: params.get('deviceName') || hostname
    });
    params.delete('remoteTools');
    params.delete('deviceName');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [user]);

  /**
   * Cross-app deep link: `/?omniSection=<helpSectionId>[&helpArticle=<id>]`
   * lands on the screen that owns a Help Center section, optionally opening the
   * guide too. Used by OmniHelp when the Help Center assistant says a ticket can
   * be resolved in Omni — the operator arrives on the right tab, not the home
   * page.
   *
   * Callers pass a SECTION id (which the assist API already returns), not a tab
   * id: the section→tab mapping lives here in `helpStore`, so it stays a single
   * source of truth instead of being copied into every consuming app.
   */
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const section = params.get('omniSection');
    const articleId = params.get('helpArticle');
    const upn = params.get('omniUser');
    if (!section && !articleId && !upn) return;

    const tabId = section ? tabForHelpSection(section) : null;
    if (tabId && isValidTab(tabId)) useUiStore.getState().setActiveTab(tabId);

    // With a person named, go all the way to their record — that's where the
    // work happens for membership/drives/host pool access, so stopping at the
    // tab would leave the operator to search for them again. Resolution is done
    // HERE because the caller only knows the UPN and openUserDetail needs the
    // AD DN.
    if (upn) {
      searchUsers(upn)
        .then((res) => {
          const wanted = upn.toLowerCase();
          const match = (res?.users ?? res ?? []).find(
            (u) => (u.UserPrincipalName || '').toLowerCase() === wanted
          );
          const dn = match?.DistinguishedName;
          if (dn) useUserMgmtStore.getState().openUserDetail?.(dn);
          // No match (Azure-only account, or no on-prem DN) is a real outcome,
          // not a silent no-op — say so, having already landed on the tab.
          else toast.info('Opened Omni', `Couldn't find an AD record for ${upn} — search for them here.`);
        })
        .catch(() => {
          toast.warning('Opened Omni', `Couldn't look up ${upn}. Search for them here.`);
        });
    }

    // An unknown/screenless section still opens the guide rather than silently
    // doing nothing, so the link is never a dead end.
    if (articleId) openHelp({ section: section || null, articleId });
    else if (section && (!tabId || !isValidTab(tabId))) openHelp({ section });

    params.delete('omniSection');
    params.delete('helpArticle');
    params.delete('omniUser');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [user, openHelp, toast]);

  if (!initialised || loading) {
    return <BusyOverlay fullscreen label="Loading Omni…" />;
  }

  if (error || !user) {
    return (
      <div className="app-error">
        <div className="app-error__panel">
          <i className="fas fa-exclamation-circle" aria-hidden="true" />
          <h2>We couldn't sign you in</h2>
          <p>{error ?? 'No user session available.'}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              useAuthStore.getState().reset();
              bootstrap();
            }}
          >
            <i className="fas fa-redo" aria-hidden="true" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <TitleBar
        onOpenGlobalSearch={() => openGlobalSearch()}
        onOpenAdminSettings={() => openAdminSettings()}
        onOpenHelp={() => openHelp()}
      />

      <Sidebar />

      <ErrorBoundary label="application">
        <TabHost />
      </ErrorBoundary>

      <GlobalSearchModal />
      <ConfirmModal />
      <WhatsNewModal />
      <ToastContainer />
      <HelpMount />
      {/* DYLAN (suite AI assistant) chat bubble — renders only with dylan-assistant and when DYLAN is up. */}
      <DylanEmbed />
      {/* Seasonal decoration (Halloween, Christmas, …) — switched on/off in Admin Settings → Seasonal themes. */}
      <Suspense fallback={null}>
        <SeasonalTheme />
      </Suspense>

      {adminSettingsOpen && (
        <Suspense fallback={<BusyOverlay fullscreen label="Loading Admin Settings…" />}>
          <AdminSettingsModal open initialTab={adminSettingsTab} onClose={() => closeAdminSettings()} />
        </Suspense>
      )}

      {/* Global Remote Access Tools host — opened from anywhere (e.g. universal
          search) via uiStore.openRemoteTools({ hostname, deviceName }). Lives at
          the root so it survives the search modal closing. */}
      {remoteToolsTarget && (
        <Suspense fallback={<BusyOverlay fullscreen label="Loading Remote Tools…" />}>
          <RemoteToolsModal
            open
            hostname={remoteToolsTarget.hostname}
            deviceName={remoteToolsTarget.deviceName || remoteToolsTarget.hostname}
            onClose={() => closeRemoteTools()}
          />
        </Suspense>
      )}
    </div>
  );
}
